/**
 * Decorative rainbow-beams backdrop (same asset as the home hero).
 *
 * Usage: render as the FIRST child of the page root, and give that root
 * `position: relative; isolation: isolate; overflow: hidden`. The backdrop
 * paints at zIndex -1, so with `isolation: isolate` on the root it sits
 * above the root's own background but below ALL child content and child
 * backgrounds — no z-index changes needed elsewhere on the page.
 *
 * `grain` overlays /noise.png (256px tile rendered at 128px for retina)
 * at 55% opacity with `mix-blend-mode: overlay` — the recipe from
 * Sintra's BackgroundV1. The grain band spans the full FRAME width
 * (anchored to the host root, not the beams), but its HEIGHT is measured
 * to end where the rainbow ends — tracking `style` overrides that move
 * or stretch the beams (width/top), with a feathered bottom edge.
 */

import {
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
} from 'react';

const BEAMS_SRC = '/images/rainbow-beams.webp';

export function RainbowBeamsBackdrop({
    style,
    grain = true,
    grainOpacity = 0.55,
}: {
    style?: CSSProperties;
    grain?: boolean;
    grainOpacity?: number;
}) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [grainHeight, setGrainHeight] = useState<number | null>(null);

    // Match the grain band's height to the rainbow's rendered bottom edge
    // (wrapper top offset + image height). Re-measures on image load and
    // resize via ResizeObserver, and on every `style` change (live tuning).
    useLayoutEffect(() => {
        if (!grain) return undefined;
        const measure = () => {
            const wrap = wrapRef.current;
            const img = imgRef.current;
            if (!wrap || !img) return;
            setGrainHeight(Math.max(0, wrap.offsetTop + img.offsetHeight));
        };
        measure();
        const observer = new ResizeObserver(measure);
        if (imgRef.current) observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, [grain, style]);

    return (
        <>
            <div
                ref={wrapRef}
                aria-hidden
                style={{
                    position: 'absolute',
                    // House style, tuned on the home hero: slightly oversized
                    // + centered (bleeds past both edges) and pulled up so
                    // the arc crests near the top of the frame. Override any
                    // of these via `style` per page.
                    top: -199,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '112%',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: -1,
                    ...style,
                }}
            >
                <img
                    ref={imgRef}
                    src={BEAMS_SRC}
                    alt=""
                    style={{ display: 'block', width: '100%', height: 'auto' }}
                />
            </div>
            {grain && grainHeight !== null && (
                <div
                    aria-hidden
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: grainHeight,
                        zIndex: -1,
                        pointerEvents: 'none',
                        backgroundImage: "url('/noise.png')",
                        backgroundSize: '128px 128px',
                        backgroundRepeat: 'repeat',
                        opacity: grainOpacity,
                        mixBlendMode: 'overlay',
                        // Feather the band's bottom edge so the grain cutoff
                        // never reads as a seam. Only the mask's ALPHA matters
                        // (white = fully shown, transparent = hidden) — the
                        // color itself never tints the page.
                        maskImage:
                            'linear-gradient(to bottom, white 75%, transparent 100%)',
                        WebkitMaskImage:
                            'linear-gradient(to bottom, white 75%, transparent 100%)',
                    }}
                />
            )}
        </>
    );
}
